import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Col, Empty, Row, Spin } from 'antd';
import { MainInfo, LiveRangeDataItem } from './details/MainInfo';
import { Timetable } from './details/Timetable';
import { Configuration } from './details/Configuration';
import { NewConfiguration } from './details/NewConfiguration';
import { ExamsPanel } from './details/ExamsPanel';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTemplateDetails,
  fetchTemplateDetailsSuccess,
  setEditedExam,
  fetchLiveRangeTemplateDetails,
} from '../templates-actions';
import { getDetailsLoading, getEditedExam, getAddedExam } from '../templates-selector';
import { getDoctorSpecial } from '../../../pace/pace-selectors';
import styles from '../../grid/BaseGrid.module.scss';
import { NewExamination } from './details/configurations/NewExamination';

export const TemplatesDetails = () => {
  const { templateId } = useParams<any>();
  const location = useLocation<{ liveRangeData?: LiveRangeDataItem[] }>();
  const { search, pathname, state: routeState } = location;
  const dispatch = useDispatch();
  const loading = useSelector(getDetailsLoading);
  const editedExam = useSelector(getEditedExam);
  const addedExam = useSelector(getAddedExam);

  // Get doctor special data from Redux (for special ranges calendar)
  const doctorSpecial = useSelector(getDoctorSpecial) as {
    weeks?: any[][];
    employeeId?: string | number;
    office?: string | number;
  };

  // Parse query parameters
  const params = new URLSearchParams(search);
  const fromLiveRanges = params.get('from') === 'liveRanges';
  const rangeType = params.get('range') || 'default';
  const employeeNum = params.get('employeeNum') || '0';
  const officeNum = params.get('officeNum') || '0';
  const dayOfWeek = params.get('dayOfWeek') || '0';
  const templateName = params.get('templateName') || '';
  const isSpecialRange = rangeType === 'special';
  const isRange0 = rangeType === 'range0';

  // Check if accessed from Template Viewer (read-only mode)
  const isReadOnly = pathname.includes('/template-viewer');

  // Build liveRangeData from route state or doctor schedule
  const liveRangeData: LiveRangeDataItem[] = useMemo(() => {
    // First try route state
    if (routeState?.liveRangeData && routeState.liveRangeData.length > 0) {
      return routeState.liveRangeData;
    }

    // Fall back to doctor special schedule data
    if (doctorSpecial?.weeks && isSpecialRange) {
      const allDays = doctorSpecial.weeks.flat().filter(Boolean);
      const data = allDays
        .filter((day: any) => day?.specialRangeTemplateId)
        .map((day: any) => ({
          startDate: day.date,
          templateId: day.specialRangeTemplateId,
          rangeNo: day.rangeNo || 0,
          employeeId: doctorSpecial.employeeId || 0,
          office: doctorSpecial.office || 0,
        }));
      return data;
    }

    return [];
  }, [routeState?.liveRangeData, doctorSpecial, isSpecialRange]);

  // Shared state for new exam slot and panel visibility
  const [showNewExam, setShowNewExam] = useState(false);
  // Session-only set of modified slot ids (kept in memory only)
  const [sessionModifiedSlots, setSessionModifiedSlots] = useState(() => new Set<number>());
  // Session-only set of newly added slot ids
  const [sessionNewSlots, setSessionNewSlots] = useState(() => new Set<number>());
  // Ref for Timetable
  const timetableRef = useRef<any>(null);
  useEffect(() => {
    if (templateId) {
      if (isSpecialRange) {
        dispatch(fetchLiveRangeTemplateDetails({ id: templateId, rangeType: rangeType }));
      } else if (isRange0) {
        dispatch(
          fetchLiveRangeTemplateDetails({
            id: templateId,
            employeeNum: employeeNum,
            officeNum: officeNum,
            dayOfWeek: dayOfWeek,
            rangeType: rangeType,
            templateName: templateName,
          })
        );
      } else {
        dispatch(fetchTemplateDetails({ id: templateId }));
      }
    }
  }, [templateId]);

  useEffect(() => {
    return () => {
      dispatch(fetchTemplateDetailsSuccess(null));
    };
  }, []);

  // Handler for Insert in NewExamination
  const handleInsertExam = (examDetails, fromEdit = false) => {
    // TODO: Update template details with new exam (API or Redux logic)
    setShowNewExam(false);
    if (examDetails && examDetails.examId && examDetails.examId !== 0) {
      if (fromEdit) {
        // Mark as modified in this session
        setSessionModifiedSlots((prev) => {
          const next = new Set(prev);
          next.add(examDetails.examId);
          return next;
        });
      } else {
        // Mark as newly added in this session
        setSessionNewSlots((prev) => {
          const next = new Set(prev);
          next.add(examDetails.examId);
          return next;
        });
        // Do not mark newly inserted slots as "modified"; they are a distinct
        // "new" state and will be highlighted with the dashed-green style.
        // Also add the event to Timetable immediately so it shows highlighted
        try {
          timetableRef.current?.addSessionNewEvent(examDetails);
        } catch (e) {
          // ignore
        }
      }
    }
    if (timetableRef.current) timetableRef.current.removeNewSlot();
  };

  // This function will be called when the + button is clicked in ExamsPanel
  const handleAddExamClick = () => {
    setShowNewExam(true); // Show the NewExamination panel
  };

  // Handler to close NewExamination without insert
  const handleCloseNewExam = (isEdit) => {
    if (isEdit) dispatch(setEditedExam(null));
    else setShowNewExam(false);
    if (timetableRef.current) timetableRef.current.removeNewSlot();
  };

  // Unify layout for both create and edit modes
  const isCreateMode = templateId == 0;
  // Only show configContent after clicking + icon (showNewExam)
  let showConfigContent = false;
  let configContent;
  if (editedExam) {
    configContent = (
      <NewConfiguration
        data={editedExam}
        onClosed={handleCloseNewExam}
        isEdit={true}
        onInserted={handleInsertExam}
        isSpecialRange={isSpecialRange}
        isRange0={isRange0}
      />
    );
    showConfigContent = true;
  } else if (showNewExam && addedExam) {
    configContent = (
      <NewConfiguration
        data={addedExam}
        onClosed={handleCloseNewExam}
        onInserted={handleInsertExam}
        isSpecialRange={isSpecialRange}
        isRange0={isRange0}
      />
    );
    showConfigContent = true;
  } else {
    configContent = <EmptyConfiguration description={<span>NO CONFIGURATION SELECTED</span>} />;
    showConfigContent = false;
  }

  return (
    <>
      <Spin spinning={loading}>
        <Row>
          <StyledLeftCol span={12}>
            <MainInfo
              isCreate={isCreateMode}
              fromLiveRanges={fromLiveRanges}
              rangeType={rangeType}
              isSpecialRange={isSpecialRange}
              isRange0={isRange0}
              liveRangeData={liveRangeData}
              initialTemplateId={templateId}
              onUpdateSuccess={() => {
                // Clear session-only highlights after successful update
                setSessionModifiedSlots(new Set());
                setSessionNewSlots(new Set());
              }}
            />
            <ExamsPanel onAddExam={handleAddExamClick} isCreate={isCreateMode} />
            {/* Show configContent only after clicking + icon */}
            {showConfigContent && configContent}
          </StyledLeftCol>
          <StyledRightCol span={12}>
            <Timetable
              ref={timetableRef}
              isCreate={isCreateMode}
              sessionModifiedSlots={Array.from(sessionModifiedSlots)}
              sessionNewSlots={Array.from(sessionNewSlots)}
              readOnly={isReadOnly}
            />
          </StyledRightCol>
        </Row>
      </Spin>
      <StyledLogoWrapper>
        <img alt='logo' className={styles['logo']} src={'./images/logo.svg'} />
      </StyledLogoWrapper>
    </>
  );
};

const StyledLeftCol = styled(Col)`
  min-width: 300px;
  padding: 10px 20px 10px;
`;

const StyledRightCol = styled(Col)`
  min-width: 150px;
  padding: 10px 20px 10px;
`;

const StyledLogoWrapper = styled.div`
  width: 100%;
  padding: 40px 40px 20px;
  display: flex;
  justify-content: flex-end;
`;

const EmptyConfiguration = styled(Empty)`
  margin-top: 100px;

  span {
    opacity: 0.6;
  }
`;
